import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import '../styles/addPost.css'; // Importer le fichier CSS pour le style
import Notification from '../components/Notification';
import CompleteProfileModal from '../components/CompleteProfileModal';
import config from '../config'; // Importer le fichier de configuration
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { CategoryContext } from '../contexts/CategoryContext';
import { CityContext } from '../contexts/CityContext';
import { UserContext } from '../contexts/UserContext';
import Resizer from 'react-image-file-resizer';
import Cookies from 'js-cookie'; // Importer js-cookie
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faImage, faVideo, faTrash } from '@fortawesome/free-solid-svg-icons';
import ButtonSpinner from '../components/ButtonSpinner';

const AddPost = () => {
    const { uuid } = useParams(); // Changed from 'id' to 'uuid' - Product UUID from URL
    const id = uuid; // Backward compatibility for existing logic referencing `id`
    const location = useLocation(); // Get navigation state
    const { categoryList: categories = [] } = useContext(CategoryContext) || {};
    const { cities = [] } = useContext(CityContext) || {};
    const { pauseTokenValidation, resumeTokenValidation } = useContext(UserContext) || {};
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        specification: '',
        price: '',
        quantity: '1', // Set default to 1
        negotiable: false,
        condition: 'Nouveau', // Set default to new
        currency: '$', // Set default to dollars
        category: '',
        city: '', // Will be set when user data is fetched
        owner: '',
        images: [],
        videos: [],
    });
    const [submitProgress, setSubmitProgress] = useState('');
    const [existingImages, setExistingImages] = useState([]);
    const [existingVideos, setExistingVideos] = useState([]);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [createdProductId, setCreatedProductId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deletedMedia, setDeletedMedia] = useState([]);
    const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [conditions] = useState([
        { value: "Nouveau", label: 'Nouveau' },
        { value: "Usagé", label: 'Usagé' },
    ]);
    const imagesInputRef = useRef(null);
    const videosInputRef = useRef(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [currencies] = useState([{ value: '$', label: 'USD' }, { value: 'CDF', label: 'FRANCS' }]);
    const [loading, setLoading] = useState(true); // Ajouter l'état de chargement
    const navigate = useNavigate();

    // Step wizard state - matching app UX
    const [currentStep, setCurrentStep] = useState(1);
    const [steps, setSteps] = useState([
        { id: 1, title: 'Infos', icon: 'information-circle', completed: false },
        { id: 2, title: 'Prix', icon: 'pricetag', completed: false },
        { id: 3, title: 'Médias', icon: 'images', completed: false },
        { id: 4, title: 'Révision', icon: 'checkmark-circle', completed: false },
    ]);
    const getMediaUrl = (url) => {
        if (!url) return '';

        // If it's an S3 URL
        if (url.includes('s3.amazonaws.com')) {
            return url.startsWith('http') ? url : `https://${url}`;
        }

        // Handle relative URLs by prepending the backend URL
        if (url.startsWith('/')) {
            return `${config.backendUrl}${url}`;
        }

        // Handle absolute URLs
        if (url.startsWith('http')) {
            return url;
        }

        // Handle partial URLs
        return `${config.backendUrl}/${url}`;

    };

    useEffect(() => {
        const token = Cookies.get('authToken');
        if (!token) {
            navigate('/login', { state: { from: '/add-post' } });
            return;
        }

        const fetchUser = async () => {
            try {
                const response = await fetch(`${config.backendUrl}/users/me/`, {
                    headers: {
                        'Authorization': `Token ${token}`,
                    },
                });
                const data = await response.json();
                if (response.ok) {
                    setCurrentUser(data); // Store current user
                    setFormData(prev => ({
                        ...prev,
                        owner: data.id,
                        city: data.city,
                    }));

                    // Check if user needs to complete date of birth and sex
                    if (!data.date_of_birth || !data.sex) {
                        setShowCompleteProfileModal(true);
                    }
                }
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        };

        fetchUser();

        // If editing a post, fetch the product details
        if (id) {
            fetchProduct();
        }
    }, [id, navigate]);
    const fetchProduct = async () => {
        if (!id) return;

        const token = Cookies.get('authToken');
        try {
            setLoading(true);
            const response = await fetch(`${config.backendUrl}/products/view/${id}/`, {
                headers: {
                    'Authorization': `Token ${token}`,
                },
            });
            const data = await response.json();

            if (response.ok) {
                setFormData(prev => ({
                    ...prev,
                    name: data.name || '',
                    description: data.description || '',
                    specification: data.specification || '',
                    price: data.price || '',
                    quantity: data.quantity || '',
                    negotiable: data.negotiable || false,
                    condition: data.condition || '',
                    currency: data.currency || '',
                    tiktok_link: data.tiktok_link || '',
                    instagram_link: data.instagram_link || '',
                    facebook_link: data.facebook_link || '',
                    category: data.category || '',
                    city: data.city || '',
                    owner: data.owner || '',
                }));

                if (data.media && Array.isArray(data.media)) {
                    setExistingImages(
                        data.media
                            .filter(media => media.type === 'image')
                            .map(image => ({
                                url: image.url,
                                type: 'image',
                                id: image.id
                            }))
                    );

                    setExistingVideos(
                        data.media
                            .filter(media => media.type === 'video')
                            .map(video => ({
                                url: video.url,
                                type: 'video',
                                id: video.id
                            }))
                    );
                }
            }
        } catch (error) {
            console.error('Error fetching product:', error);
            setNotification({
                message: 'Error loading product data',
                type: 'error',
                header: 'Error'
            });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        // Check if product data was passed via navigation state (from ProductDetails page)
        const { productData, fromProductDetails } = location.state || {};
        
        if (id && productData && fromProductDetails) {
            // Use the passed data directly - no API call needed
            setFormData(prev => ({
                ...prev,
                name: productData.name || '',
                description: productData.description || '',
                specification: productData.specification || '',
                price: productData.price || '',
                quantity: productData.quantity || '',
                negotiable: productData.negotiable || false,
                condition: productData.condition || '',
                currency: productData.currency || '',
                tiktok_link: productData.tiktok_link || '',
                instagram_link: productData.instagram_link || '',
                facebook_link: productData.facebook_link || '',
                category: productData.category || '',
                city: productData.city || '',
                owner: productData.owner || '',
            }));

            if (productData.media && Array.isArray(productData.media)) {
                setExistingImages(
                    productData.media
                        .filter(media => media.type === 'image')
                        .map(image => ({
                            url: image.url,
                            type: 'image',
                            id: image.id
                        }))
                );

                setExistingVideos(
                    productData.media
                        .filter(media => media.type === 'video')
                        .map(video => ({
                            url: video.url,
                            type: 'video',
                            id: video.id
                        }))
                );
            }
            
            setLoading(false);
        } else {
            // Fall back to API call if data wasn't passed
            fetchProduct();
        }
    }, [id, location.state]);

    useEffect(() => {
        const checkFormValidity = () => {
            const descriptionText = (formData.description || '').replace(/<[^>]*>/g, '').trim();
            const nameText = (formData.name || '').trim();
            
            // Log validation state for debugging
            console.log('Form Validation Check:', {
                name: nameText,
                nameLength: nameText.length,
                nameValid: nameText.length >= 5,
                descriptionLength: descriptionText.length,
                descriptionValid: descriptionText.length >= 50,
                price: formData.price,
                priceValid: !!formData.price && !isNaN(Number(formData.price)) && Number(formData.price) > 0,
                quantity: formData.quantity,
                quantityValid: !!formData.quantity,
                condition: formData.condition,
                conditionValid: !!formData.condition,
                category: formData.category,
                categoryValid: !!formData.category,
                city: formData.city,
                cityValid: !!formData.city,
                currency: formData.currency,
                currencyValid: !!formData.currency,
                imagesCount: formData.images.length,
                existingImagesCount: existingImages.length,
            });
            
            const hasRequiredFields =
                nameText.length >= 5 &&  // Match app requirement (5 chars min)
                descriptionText.length >= 50 &&
                formData.price &&
                !isNaN(Number(formData.price)) &&
                Number(formData.price) > 0 &&
                formData.quantity &&
                formData.condition &&
                formData.category &&
                formData.city &&
                formData.currency;

            // Only check for images on new posts
            if (!id) {
                const hasImages = formData.images.length > 0 || existingImages.length > 0;
                console.log('Final validation (new post):', { hasRequiredFields, hasImages, isValid: hasRequiredFields && hasImages });
                setIsFormValid(hasRequiredFields && hasImages);
            } else {
                console.log('Final validation (edit):', { hasRequiredFields, isValid: hasRequiredFields });
                setIsFormValid(hasRequiredFields);
            }
        };

        checkFormValidity();
    }, [
        formData.name,
        formData.description,
        formData.price,
        formData.quantity,
        formData.condition,
        formData.category,
        formData.city,
        formData.currency,
        formData.images,
        existingImages,
        id // Add id to dependencies
    ]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
        validateForm();
    };

    const handleQuillChange = (name, value) => {
        const plainText = value.replace(/<[^>]*>/g, '').trim();

        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));

        // Update error state based on description length
        if (name === 'description') {
            if (plainText.length < 50) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    description: 'La description doit contenir au moins 50 caractères'
                }));
            } else {
                setErrors(prevErrors => {
                    const { description, ...rest } = prevErrors;
                    return rest;
                });
            }
        }
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        const newFiles = Array.from(files);
        const updatedFiles = [...formData[name]];

        // Check total count including existing media
        if (name === 'images') {
            const totalImages = existingImages.length + updatedFiles.length + newFiles.length;
            if (totalImages > 6) {
                const remaining = 6 - existingImages.length - updatedFiles.length;
                setNotification({ 
                    message: `Vous pouvez ajouter jusqu'à ${remaining} image(s) supplémentaire(s). Total maximum: 6 images.`, 
                    type: 'error', 
                    header: 'Limite atteinte' 
                });
                return;
            }
        }

        if (name === 'videos') {
            const totalVideos = existingVideos.length + updatedFiles.length + newFiles.length;
            if (totalVideos > 2) {
                const remaining = 2 - existingVideos.length - updatedFiles.length;
                setNotification({ 
                    message: `Vous pouvez ajouter jusqu'à ${remaining} vidéo(s) supplémentaire(s). Total maximum: 2 vidéos.`, 
                    type: 'error', 
                    header: 'Limite atteinte' 
                });
                return;
            }
        }

        newFiles.forEach(file => {
            if (file.type.startsWith('image/')) {
                Resizer.imageFileResizer(
                    file,
                    800, // Max width
                    800, // Max height
                    'JPEG', // Format
                    100, // Quality
                    0, // Rotation
                    (uri) => {
                        updatedFiles.push(uri);
                        // Check if all files have been processed
                        if (updatedFiles.length === formData[name].length + newFiles.length) {
                            setFormData(prevFormData => ({
                                ...prevFormData,
                                [name]: updatedFiles,
                            }));
                            // Reset the file input
                            if (e.target) {
                                e.target.value = '';
                            }
                        }
                    },
                    'blob' // Output type
                );
            } else {
                updatedFiles.push(file);
                // Check if all files have been processed
                if (updatedFiles.length === formData[name].length + newFiles.length) {
                    setFormData(prevFormData => ({
                        ...prevFormData,
                        [name]: updatedFiles,
                    }));
                    // Reset the file input
                    if (e.target) {
                        e.target.value = '';
                    }
                }
            }
        });
    };

    const validateForm = () => {
        const newErrors = {};
        const descriptionText = formData.description.replace(/<[^>]*>/g, '').trim();

        // Basic validation checks
        if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
        if (descriptionText.length < 50) newErrors.description = 'La description doit contenir au moins 50 caractères';
        if (!formData.price) newErrors.price = 'Le prix est requis';
        if (!formData.quantity) newErrors.quantity = 'La quantité est requise';
        if (!formData.condition) newErrors.condition = 'L\'état est requis';
        if (!formData.category) newErrors.category = 'La catégorie est requise';
        if (!formData.city) newErrors.city = 'La ville est requise';
        if (!formData.currency) newErrors.currency = 'La devise est requise';

        // Image validation - only check for new posts
        if (!id) {
            const hasImages = formData.images.length > 0 || existingImages.length > 0;
            if (!hasImages) newErrors.images = 'Au moins une image est requise';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if user has completed date of birth and sex
        if (currentUser && (!currentUser.date_of_birth || !currentUser.sex)) {
            setShowCompleteProfileModal(true);
            return;
        }

        if (!validateForm()) return;

        // Pause token validation during form submission to prevent interference
        if (pauseTokenValidation) {
            pauseTokenValidation();
        }

        setIsSubmitting(true);
        setSubmitProgress('Préparation de l\'envoi...');

        const formDataToSend = new FormData();

        // Add text fields
        Object.keys(formData).forEach(key => {
            if (key !== 'images' && key !== 'videos') {
                formDataToSend.append(key, formData[key]);
            }
        });

        // Handle media operations for updates
        if (id) {
            // Extract valid IDs from existing media, excluding those marked for deletion
            const currentImageIds = existingImages
                .filter(img => !deletedMedia.includes(img.id)) // Exclude deleted media
                .map(img => img.id)
                .filter(Boolean); // Filter out null/undefined
            
            const currentVideoIds = existingVideos
                .filter(vid => !deletedMedia.includes(vid.id)) // Exclude deleted media
                .map(vid => vid.id)
                .filter(Boolean); // Filter out null/undefined

            // Media operations object
            const mediaOperations = {
                keep: [...currentImageIds, ...currentVideoIds],
                delete: Array.from(new Set(deletedMedia)).filter(id => id !== null) // Filter out null/undefined
            };

            ; // Debugging
            formDataToSend.append('media_operations', JSON.stringify(mediaOperations));
        }

        // Add new media files
        formData.images.forEach((image, index) => {
            formDataToSend.append('new_images', image, `image-${index}.jpg`);
        });

        formData.videos.forEach((video, index) => {
            formDataToSend.append('new_videos', video, `video-${index}.mp4`);
        });

        try {
            setSubmitProgress('Envoi des données...');
            const token = Cookies.get('authToken');
            const url = uuid ? `${config.backendUrl}/products/edit/${uuid}/` : `${config.backendUrl}/products/`;  // Use uuid

            const response = await fetch(url, {
                method: uuid ? 'PUT' : 'POST',  // Use uuid to determine method
                headers: {
                    'Authorization': `Token ${token}`
                },
                body: formDataToSend
            });

            if (!response.ok) {
                // Handle 401 Unauthorized - token might be expired
                if (response.status === 401) {
                    // Resume token validation before redirecting
                    if (resumeTokenValidation) {
                        resumeTokenValidation();
                    }
                    
                    setNotification({
                        message: 'Votre session a expiré. Veuillez vous reconnecter.',
                        type: 'error',
                        header: 'Session expirée'
                    });
                    setTimeout(() => {
                        Cookies.remove('authToken');
                        navigate('/login', { state: { from: window.location.pathname } });
                    }, 2000);
                    return;
                }
                
                const contentType = response.headers.get("content-type");
                let errorData;

                if (contentType && contentType.indexOf("application/json") !== -1) {
                    errorData = await response.json();
                } else {
                    const textError = await response.text();
                    throw new Error(textError);
                }

                throw new Error(JSON.stringify(errorData));
            }

            const responseData = await response.json();
            setSubmitProgress('Finalisation...');
            setCreatedProductId(responseData.id);

            setNotification({
                message: `Post ${id ? 'mis à jour' : 'ajouté'} avec succès!`,
                type: 'success',
                header: 'Succès'
            });

            // Clear deleted media array after successful update
            setDeletedMedia([]);

            setTimeout(() => {
                setNotification({ message: '', type: '', header: '' });
                // Use UUID from response or the one from URL params
                navigate(`/products/view/${responseData.uuid || uuid || responseData.id}`);
            }, 2000);

        } catch (error) {
            console.error('Submit error:', error);
            setSubmitProgress('');

            let errorMessage;
            try {
                const errorData = JSON.parse(error.message);
                
                // Check for content moderation rejection
                const errorString = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
                if (errorString.includes('inapproprié') || errorString.includes('inappropriate')) {
                    errorMessage = errorData.detail || errorData.error || errorData.message || 
                                  'Une ou plusieurs images contiennent du contenu inapproprié et ont été rejetées. Veuillez choisir des images appropriées.';
                    setNotification({
                        message: errorMessage,
                        type: 'error',
                        header: '⚠️ Images rejetées'
                    });
                    setIsSubmitting(false);
                    return;
                }
                
                errorMessage = errorData.detail ||
                    errorData.error ||
                    Object.values(errorData)[0]?.[0] ||
                    `Une erreur s'est produite lors de ${id ? 'la mise à jour' : 'l\'ajout'} du post`;
            } catch {
                errorMessage = error.message ||
                    `Une erreur s'est produite lors de ${id ? 'la mise à jour' : 'l\'ajout'} du post`;
            }

            setNotification({
                message: errorMessage,
                type: 'error',
                header: 'Erreur'
            });
        } finally {
            setIsSubmitting(false);
            
            // Always resume token validation when submission is complete
            if (resumeTokenValidation) {
                resumeTokenValidation();
            }
        }
    };
    const handleDeleteMedia = (id, type, index) => {
        if (!id) {
            console.error('Invalid media ID:', id); // Debugging
            return;
        }

        try {
            // Add the ID to deletedMedia first
            setDeletedMedia(prev => {
                const newDeletedMedia = [...prev, id];
                ; // Debugging
                return newDeletedMedia;
            });

            // Then remove from the appropriate array
            if (type === 'image') {
                setExistingImages(prev => prev.filter((_, idx) => idx !== index));
            } else {
                setExistingVideos(prev => prev.filter((_, idx) => idx !== index));
            }
        } catch (error) {
            console.error('Error deleting media:', error);
            setNotification({
                message: 'Une erreur est survenue lors de la suppression du média',
                type: 'error',
                header: 'Erreur'
            });
        }
    };

    // Individual media deletion function (for immediate deletion via API)
    const handleDeleteMediaImmediate = async (id, type, index) => {
        if (!id) {
            console.error('Invalid media ID:', id);
            return;
        }

        try {
            const confirmDelete = window.confirm(
                `Êtes-vous sûr de vouloir supprimer ce ${type === 'image' ? 'image' : 'vidéo'} définitivement ?`
            );
            
            if (!confirmDelete) return;

            const token = Cookies.get('authToken');
            const endpoint = type === 'image' 
                ? `${config.backendUrl}/products/product-images/${id}/`
                : `${config.backendUrl}/products/product-videos/${id}/`;

            const response = await fetch(endpoint, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                // Remove from UI immediately
                if (type === 'image') {
                    setExistingImages(prev => prev.filter((_, idx) => idx !== index));
                } else {
                    setExistingVideos(prev => prev.filter((_, idx) => idx !== index));
                }

                setNotification({
                    message: `${type === 'image' ? 'Image' : 'Vidéo'} supprimée avec succès`,
                    type: 'success',
                    header: 'Succès'
                });
            } else {
                throw new Error(`Failed to delete ${type}: ${response.status}`);
            }
        } catch (error) {
            console.error(`Error deleting ${type} immediately:`, error);
            setNotification({
                message: `Erreur lors de la suppression du ${type === 'image' ? 'image' : 'vidéo'}`,
                type: 'error',
                header: 'Erreur'
            });
        }
    };

    const handleNotificationClose = () => {
        setNotification({ message: '', type: '', header: '' });
    };
    // Add these functions to handle drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e, type) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer.files;
        handleFileChange({ target: { name: type, files } });
    };
    const handleDeleteImage = (index) => {
        const newImages = [...formData.images];
        newImages.splice(index, 1);
        setFormData({ ...formData, images: newImages });
    };

    const handleDeleteVideo = (index) => {
        const newVideos = [...formData.videos];
        newVideos.splice(index, 1);
        setFormData({ ...formData, videos: newVideos });
    };

    // Step validation functions
    const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '').trim();
    
    const validateStep1 = () => {
        const nameText = (formData.name || '').trim();
        const descText = stripHtml(formData.description);
        
        const isValid = !!(
            nameText.length >= 5 &&  // Reduced from 10 to 5 to match app
            nameText.length <= 255 &&
            descText.length >= 50 &&  // Match isFormValid requirement
            formData.category &&
            formData.city
        );
        
        console.log('Step 1 Validation:', {
            nameLength: nameText.length,
            nameValid: nameText.length >= 5 && nameText.length <= 255,
            descLength: descText.length,
            descValid: descText.length >= 50,
            category: formData.category,
            city: formData.city,
            isValid
        });
        
        return isValid;
    };

    const validateStep2 = () => {
        const price = String(formData.price || '').trim();
        const quantity = String(formData.quantity || '').trim();
        
        const isValid = !!(
            price &&
            !isNaN(Number(price)) &&
            Number(price) > 0 &&
            quantity &&
            !isNaN(Number(quantity)) &&
            Number(quantity) > 0 &&
            formData.condition &&
            formData.currency
        );
        
        console.log('Step 2 Validation:', {
            price,
            priceValid: price && !isNaN(Number(price)) && Number(price) > 0,
            quantity,
            quantityValid: quantity && !isNaN(Number(quantity)) && Number(quantity) > 0,
            condition: formData.condition,
            currency: formData.currency,
            isValid
        });
        
        return isValid;
    };

    const validateStep3 = () => {
        const totalImages = formData.images.length + existingImages.length;
        const isValid = totalImages > 0;
        
        console.log('Step 3 Validation:', {
            newImages: formData.images.length,
            existingImages: existingImages.length,
            totalImages,
            isValid
        });
        
        return isValid;
    };

    const handleNextStep = () => {
        let isValid = false;
        
        if (currentStep === 1) isValid = validateStep1();
        else if (currentStep === 2) isValid = validateStep2();
        else if (currentStep === 3) isValid = validateStep3();
        
        if (isValid || currentStep === 4) {
            // Mark current step as completed
            const newSteps = [...steps];
            newSteps[currentStep - 1].completed = true;
            setSteps(newSteps);
            
            if (currentStep < 4) {
                setCurrentStep(currentStep + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            setNotification({
                message: 'Veuillez remplir tous les champs obligatoires avant de continuer.',
                type: 'warning',
                header: 'Champs requis'
            });
        }
    };

    const handlePreviousStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Update step completion status when form data changes
    useEffect(() => {
        const newSteps = [...steps];
        newSteps[0].completed = validateStep1();
        newSteps[1].completed = validateStep2();
        newSteps[2].completed = validateStep3();
        setSteps(newSteps);
    }, [formData, existingImages]);

    // Render step 1: Product Information
    const renderStep1 = () => (
        <div className="step-content">
            <h3 className="step-header">Informations du produit</h3>
            
            <div className={`form-group ${errors.name ? 'form-group-error' : ''} ${!errors.name && formData.name ? 'form-group-valid' : ''}`}>
                <label htmlFor="name">Nom du produit <span className={`required ${!errors.name && formData.name ? 'valid' : ''}`}>
                    {!errors.name && formData.name ? '✔' : '*'}
                </span></label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    maxLength="255"
                    placeholder="Entrez le nom du produit"
                    required
                    disabled={isSubmitting}
                />
                <small className="field-helper">{formData.name.length}/255 caractères (min. 10)</small>
            </div>

            <div className={`form-group ${errors.description ? 'form-group-error' : ''} ${!errors.description && formData.description ? 'form-group-valid' : ''}`}>
                <label htmlFor="description">Description <span className={`required ${!errors.description && formData.description ? 'valid' : ''}`}>
                    {!errors.description && formData.description ? '✔' : '*'}
                </span></label>
                <ReactQuill
                    id="description"
                    name="description"
                    value={formData.description || ''}
                    onChange={(value) => handleQuillChange('description', value)}
                    modules={{
                        clipboard: { matchVisual: false },
                        toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }]],
                    }}
                    placeholder="Décrivez votre produit en détail"
                    className={errors.description ? 'error' : ''}
                />
                {errors.description && (
                    <span className="add-post-error-message">{errors.description}</span>
                )}
            </div>

            <div className={`form-group ${errors.specification ? 'form-group-error' : ''} ${!errors.specification && formData.specification ? 'form-group-valid' : ''}`}>
                <label htmlFor="specification">Spécification (optionnel)</label>
                <ReactQuill
                    id="specification"
                    name="specification"
                    value={formData.specification || ''}
                    onChange={(value) => handleQuillChange('specification', value)}
                    modules={{
                        clipboard: { matchVisual: false },
                        toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }]],
                    }}
                    placeholder="Spécifications techniques (optionnel)"
                    className={errors.specification ? 'error' : ''}
                />
            </div>

            <div className={`form-group ${errors.category ? 'form-group-error' : ''} ${!errors.category && formData.category ? 'form-group-valid' : ''}`}>
                <label htmlFor="category">Catégorie <span className={`required ${!errors.category && formData.category ? 'valid' : ''}`}>
                    {!errors.category && formData.category ? '✔' : '*'}
                </span></label>
                <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                >
                    <option value="">Sélectionner une catégorie</option>
                    {categories && categories.length > 0 ? (
                        categories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))
                    ) : (
                        <option disabled>Chargement des catégories...</option>
                    )}
                </select>
            </div>

            <div className={`form-group ${errors.city ? 'form-group-error' : ''} ${!errors.city && formData.city ? 'form-group-valid' : ''}`}>
                <label htmlFor="city">Ville <span className={`required ${!errors.city && formData.city ? 'valid' : ''}`}>
                    {!errors.city && formData.city ? '✔' : '*'}
                </span></label>
                <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                >
                    <option value="">Sélectionner une ville</option>
                    {cities && cities.length > 0 ? (
                        cities.map((city) => (
                            <option key={city.id} value={city.id}>
                                {city.name}
                            </option>
                        ))
                    ) : (
                        <option disabled>Chargement des villes...</option>
                    )}
                </select>
            </div>
        </div>
    );

    // Render step 2: Pricing
    const renderStep2 = () => (
        <div className="step-content">
            <h3 className="step-header">Prix et détails</h3>
            
            <div className={`form-group ${errors.price ? 'form-group-error' : ''} ${!errors.price && formData.price ? 'form-group-valid' : ''}`}>
                <label htmlFor="price">Prix <span className={`required ${!errors.price && formData.price ? 'valid' : ''}`}>
                    {!errors.price && formData.price ? '✔' : '*'}
                </span></label>
                <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="Entrez le prix"
                    required
                    disabled={isSubmitting}
                />
            </div>

            <div className={`form-group ${errors.currency ? 'form-group-error' : ''} ${!errors.currency && formData.currency ? 'form-group-valid' : ''}`}>
                <label htmlFor="currency">Devise <span className={`required ${!errors.currency && formData.currency ? 'valid' : ''}`}>
                    {!errors.currency && formData.currency ? '✔' : '*'}
                </span></label>
                <select id="currency" name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                >
                    <option value="">Sélectionner une devise</option>
                    {currencies.map((currency) => (
                        <option key={currency.value} value={currency.value}>
                            {currency.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={`form-group ${errors.quantity ? 'form-group-error' : ''} ${!errors.quantity && formData.quantity ? 'form-group-valid' : ''}`}>
                <label htmlFor="quantity">Quantité <span className={`required ${!errors.quantity && formData.quantity ? 'valid' : ''}`}>
                    {!errors.quantity && formData.quantity ? '✔' : '*'}
                </span></label>
                <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder="Quantité disponible"
                    required
                    disabled={isSubmitting}
                />
            </div>

            <div className={`form-group ${errors.condition ? 'form-group-error' : ''} ${!errors.condition && formData.condition ? 'form-group-valid' : ''}`}>
                <label htmlFor="condition">État <span className={`required ${!errors.condition && formData.condition ? 'valid' : ''}`}>
                    {!errors.condition && formData.condition ? '✔' : '*'}
                </span></label>
                <select
                    id="condition"
                    name="condition"
                    value={formData.condition}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                >
                    <option value="">Sélectionner un état</option>
                    {conditions.map((condition) => (
                        <option key={condition.value} value={condition.value}>
                            {condition.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={`form-group ${errors.negotiable ? 'form-group-error' : ''} ${!errors.negotiable && formData.negotiable ? 'form-group-valid' : ''}`}>
                <label htmlFor="negotiable" className="checkbox-label">
                    <input
                        type="checkbox"
                        id="negotiable"
                        name="negotiable"
                        checked={formData.negotiable}
                        onChange={handleChange}
                        disabled={isSubmitting}
                    />
                    <span>Prix négociable</span>
                </label>
            </div>
        </div>
    );

    // Render step 3: Media
    const renderStep3 = () => (
        <div className="step-content">
            <h3 className="step-header">Images et vidéos</h3>
            
            <div className="form-group">
                <label htmlFor="images">
                    Images <span className={`required ${!errors.images && (formData.images.length > 0 || existingImages.length > 0) ? 'valid' : ''}`}>
                        {!errors.images && (formData.images.length > 0 || existingImages.length > 0) ? '✔' : '*'}
                    </span>
                </label>
                <div
                    className="file-dropzone"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'images')}
                    onClick={() => document.getElementById('images').click()}
                >
                    <FontAwesomeIcon icon={faImage} size="2x" />
                    <p>Glissez et déposez vos images ici ou cliquez pour sélectionner</p>
                    <input
                        type="file"
                        id="images"
                        name="images"
                        multiple
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={isSubmitting}
                        style={{ display: 'none' }}
                    />
                </div>
                {(formData.images.length > 0 || existingImages.length > 0) && (
                    <small className="field-helper">
                        {formData.images.length + existingImages.length} image(s) • 
                        Glissez pour réorganiser • Cliquez sur X pour supprimer
                    </small>
                )}
                <div className="media-preview">
                    {existingImages.map((image, index) => (
                        <div key={`existing-${index}`} className="media-item">
                            <img src={getMediaUrl(image.url)} alt={`Image existante ${index + 1}`} />
                            <div className="media-controls">
                                <button
                                    type="button"
                                    className="delete-icon batch-delete"
                                    onClick={() => handleDeleteMedia(image.id, 'image', index)}
                                    title="Supprimer à la soumission"
                                    disabled={isSubmitting}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                                <button
                                    type="button"
                                    className="delete-icon immediate-delete"
                                    onClick={() => handleDeleteMediaImmediate(image.id, 'image', index)}
                                    title="Supprimer immédiatement"
                                    disabled={isSubmitting}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    ))}
                    {formData.images.map((image, index) => (
                        <div key={`new-${index}`} className="media-item">
                            <img src={URL.createObjectURL(image)} alt={`Nouvelle image ${index + 1}`} />
                            <button 
                                type="button" 
                                className="delete-icon" 
                                onClick={() => handleDeleteImage(index)}
                                disabled={isSubmitting}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="videos">Vidéos (optionnel)</label>
                <div
                    className="file-dropzone"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'videos')}
                    onClick={() => document.getElementById('videos').click()}
                >
                    <FontAwesomeIcon icon={faVideo} size="2x" />
                    <p>Glissez et déposez vos vidéos ici ou cliquez pour sélectionner</p>
                    <input
                        type="file"
                        id="videos"
                        name="videos"
                        multiple
                        accept="video/*"
                        onChange={handleFileChange}
                        disabled={isSubmitting}
                        style={{ display: 'none' }}
                    />
                </div>
                {(formData.videos.length > 0 || existingVideos.length > 0) && (
                    <small className="field-helper">
                        {formData.videos.length + existingVideos.length} vidéo(s) • 
                        Taille max recommandée: 50MB par vidéo
                    </small>
                )}
                <div className="media-preview">
                    {existingVideos.map((video, index) => (
                        <div key={`existing-${index}`} className="media-item">
                            <video src={getMediaUrl(video.url)} controls />
                            <div className="media-controls">
                                <button
                                    type="button"
                                    className="delete-icon batch-delete"
                                    onClick={() => handleDeleteMedia(video.id, 'video', index)}
                                    title="Supprimer à la soumission"
                                    disabled={isSubmitting}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                                <button
                                    type="button"
                                    className="delete-icon immediate-delete"
                                    onClick={() => handleDeleteMediaImmediate(video.id, 'video', index)}
                                    title="Supprimer immédiatement"
                                    disabled={isSubmitting}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    ))}
                    {formData.videos.map((video, index) => (
                        <div key={`new-${index}`} className="media-item">
                            <video src={URL.createObjectURL(video)} controls />
                            <button 
                                type="button" 
                                className="delete-icon" 
                                onClick={() => handleDeleteVideo(index)}
                                disabled={isSubmitting}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    // Render step 4: Review
    const renderStep4 = () => (
        <div className="step-content">
            <h3 className="step-header">Révision et publication</h3>
            <div className="review-section">
                <div className="review-item">
                    <strong>Nom:</strong> {formData.name || 'Non renseigné'}
                </div>
                <div className="review-item">
                    <strong>Catégorie:</strong> {categories.find(c => c.id === formData.category)?.name || 'Non renseignée'}
                </div>
                <div className="review-item">
                    <strong>Ville:</strong> {cities.find(c => c.id === formData.city)?.name || 'Non renseignée'}
                </div>
                <div className="review-item">
                    <strong>Prix:</strong> {formData.price} {formData.currency}
                </div>
                <div className="review-item">
                    <strong>Quantité:</strong> {formData.quantity}
                </div>
                <div className="review-item">
                    <strong>État:</strong> {formData.condition}
                </div>
                <div className="review-item">
                    <strong>Négociable:</strong> {formData.negotiable ? 'Oui' : 'Non'}
                </div>
                <div className="review-item">
                    <strong>Images:</strong> {formData.images.length + existingImages.length} image(s)
                </div>
                <div className="review-item">
                    <strong>Vidéos:</strong> {formData.videos.length + existingVideos.length} vidéo(s)
                </div>
            </div>
            {submitProgress && (
                <div className="submit-progress">{submitProgress}</div>
            )}
        </div>
    );

    return (
        <div className="add-post-container">
            <div className='add-post-container-card'>
                {/* Header */}
                <div className="wizard-header">
                    <h2>{id ? 'Modifier le produit' : 'Nouveau produit'}</h2>
                </div>

                {/* Progress Bar */}
                <div className="progress-container">
                    <div className="progress-bar-bg">
                        <div 
                            className="progress-bar-fill" 
                            style={{ width: `${(currentStep / 4) * 100}%` }}
                        ></div>
                    </div>
                    
                    {/* Step Indicators */}
                    <div className="step-indicators">
                        {steps.map((step, index) => (
                            <div key={step.id} className="step-item">
                                <div className={`step-circle ${index + 1 === currentStep ? 'active' : ''} ${step.completed ? 'completed' : ''}`}>
                                    {step.completed ? '✓' : index + 1}
                                </div>
                                <div className={`step-title ${index + 1 === currentStep ? 'active' : ''}`}>
                                    {step.title}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="add-post-form">
                    {/* Render current step */}
                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                    {currentStep === 4 && renderStep4()}

                    {/* Navigation Buttons */}
                    <div className="form-buttons wizard-buttons">
                        {currentStep > 1 && (
                            <button 
                                type="button" 
                                className="prev-button" 
                                onClick={handlePreviousStep}
                                disabled={isSubmitting}
                            >
                                ← Précédent
                            </button>
                        )}
                        
                        {currentStep < 4 ? (
                            <button 
                                type="button" 
                                className="next-button" 
                                onClick={handleNextStep}
                                disabled={isSubmitting}
                            >
                                Suivant →
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className={`add-post-submit-button ${isFormValid ? 'enabled' : 'disabled'} ${isSubmitting ? 'loading' : ''}`}
                                disabled={!isFormValid || isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="submit-spinner"></span>
                                        {id ? 'Mise à jour en cours...' : 'Publication en cours...'}
                                    </>
                                ) : (
                                    id ? 'Mettre à jour' : 'Publier'
                                )}
                            </button>
                        )}
                    </div>
                </form>
                
                {notification.message && (
                    <Notification
                        message={notification.message}
                        type={notification.type}
                        header={notification.header}
                        onClose={handleNotificationClose}
                    />
                )}

                <CompleteProfileModal
                    isOpen={showCompleteProfileModal}
                    onClose={() => setShowCompleteProfileModal(false)}
                    onComplete={(updatedUser) => {
                        setCurrentUser(updatedUser);
                        setShowCompleteProfileModal(false);
                    }}
                    requiredFields={['date_of_birth', 'sex']}
                />
            </div>
        </div>
    );
};

export default AddPost;